import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ModuleKey, ActionKey } from '@alfin-crm/contracts';

import { DEFAULT_CURRENCY, getCurrencySymbol, normalizeCurrency } from '@/lib/currency';

export interface CompanySettingsSnapshot {
  currency: string;
  timezone: string;
  dateFormat: string;
}

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  companyId: string;
  companyName: string;
  teamId: string | null;
  roleId: string;
  roleName: string;
  /** Array of "module:action" strings */
  permissions: string[];
  avatarUrl?: string;
  companySettings?: CompanySettingsSnapshot;
}

export interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  permissions: string[];
  isAuthenticated: boolean;
  requires2FA: boolean;
  isLoading: boolean;
  /** True while an Admin/Super Admin is logged in as another user */
  isImpersonating: boolean;
}


const initialState: AuthState = {
  user: null,
  accessToken: null,
  permissions: [],
  isAuthenticated: false,
  requires2FA: false,
  isLoading: true, // true on first load until we verify the session
  isImpersonating: false,
};


export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
      state.permissions = action.payload.permissions;
      state.isAuthenticated = true;
      state.requires2FA = false;
      state.isLoading = false;
    },
    /** Alias used by login/2FA/impersonation flows */
    setCredentials(
      state,
      action: PayloadAction<{
        accessToken: string;
        user: AuthUser;
        isImpersonating?: boolean;
      }>,
    ) {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.permissions = action.payload.user.permissions;
      state.isAuthenticated = true;
      state.requires2FA = false;
      state.isLoading = false;
      state.isImpersonating = Boolean(action.payload.isImpersonating);
    },
    clearUser(state) {
      state.user = null;
      state.accessToken = null;
      state.permissions = [];
      state.isAuthenticated = false;
      state.requires2FA = false;
      state.isLoading = false;
      state.isImpersonating = false;
    },
    /** Alias used by logout handler */
    clearAuth(state) {
      state.user = null;
      state.accessToken = null;
      state.permissions = [];
      state.isAuthenticated = false;
      state.requires2FA = false;
      state.isLoading = false;
      state.isImpersonating = false;
    },
    setRequires2FA(state) {
      state.requires2FA = true;
      state.isLoading = false;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
  },
});

export const { setUser, clearUser, setCredentials, clearAuth, setRequires2FA, setLoading } = authSlice.actions;

// ---- Selectors ----
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectAccessToken = (state: { auth: AuthState }) => state.auth.accessToken;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectIsLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectIsImpersonating = (state: { auth: AuthState }) => state.auth.isImpersonating;

export const selectCompanyCurrency = (state: { auth: AuthState }) =>
  normalizeCurrency(state.auth.user?.companySettings?.currency ?? DEFAULT_CURRENCY);

export const selectCompanyCurrencySymbol = (state: { auth: AuthState }) =>
  getCurrencySymbol(selectCompanyCurrency(state));

/** Returns true if the current user has the given permission */
export const selectHasPermission =
  (module: ModuleKey, action: ActionKey) =>
  (state: { auth: AuthState }): boolean => {
    const permissions = state.auth.user?.permissions ?? [];
    return permissions.includes(`${module}:${action}`);
  };

export default authSlice.reducer;
